import { Icon } from '@iconify/react'
import { useEffect, useMemo } from 'react'
import useConvertToCurrency from 'hooks/useConvertToCurrency'
import { FeeAmount } from '@echodex/v3-sdk'
import { Flex, Text, TooltipText, useTooltip } from '@echodex/uikit'
import { useActiveChainId } from 'hooks/useActiveChainId'
import { useCakePriceAsBN } from 'hooks/useCakePriceAsBN'
import { usePoolAvgInfo } from 'hooks/usePoolAvgInfo'
import { V3AllPoolsQuery } from 'graphql/v3/generated/types-and-hooks'
import { useFarm } from 'hooks/useFarm'
import useSWR from 'swr'
import { FarmTVL, createFarmFetcherV3 } from '@echodex/farms'
import fetchWithTimeout from 'utils/fetchWithTimeout'
import { FARMS_API } from 'config/constants/endpoints'
import { getViemClients } from 'utils/viem'
import { FAST_INTERVAL } from 'config/constants'
import { useTranslation } from '@echodex/localization'

interface V3AprProps {
    poolInfo: V3AllPoolsQuery['pools'][0]
    setCanFarm: (canFarm: boolean) => void
}

const farmFetcherV3 = createFarmFetcherV3(getViemClients)

const V3AprWithFarming = ({ poolInfo, setCanFarm }: V3AprProps) => {
    const { t } = useTranslation()
    const { chainId } = useActiveChainId()
    const currencyA = useConvertToCurrency(poolInfo.token0, false)
    const currencyB = useConvertToCurrency(poolInfo.token1, false)
    const feeAmount = parseInt(poolInfo.feeTier) as FeeAmount

    const { data: farm } = useFarm({ currencyA: currencyA ?? undefined, currencyB: currencyB ?? undefined, feeAmount })

    const cakePrice = useCakePriceAsBN()

    const { data: farmApr } = useSWR<FarmTVL>(
        [chainId, 'cake-apr-tvl', farm],
        async () => {
            const result = await fetchWithTimeout(`${FARMS_API}/v3/${chainId}/liquidity/${farm.farm.lpAddress}`)
                .then((r) => r.json())
                .catch((err) => {
                    console.error(err)
                    throw err
                })

            const { activeTvlUSD, activeTvlUSDUpdatedAt, cakeApr } = farmFetcherV3.getCakeAprAndTVL(
                farm.farm,
                result.formatted,
                cakePrice.toString(),
                farm.cakePerSecond,
            )

            return {
                cakeApr,
                activeTvlUSD,
                activeTvlUSDUpdatedAt,
            }
        },
        {
            refreshInterval: FAST_INTERVAL,
            keepPreviousData: true,
        },
    )

    const { feeUSD, tvlUSD } = usePoolAvgInfo({
        address: poolInfo.id,
        chainId,
    })

    const cakeApr = farmApr && farmApr.cakeApr ? parseFloat(farmApr.cakeApr) : 0
    const globalLpApr = useMemo(() => (tvlUSD ? (100 * feeUSD * 365) / tvlUSD : 0), [feeUSD, tvlUSD])
    const displayApr = globalLpApr + cakeApr

    useEffect(() => {
        setCanFarm(!!farmApr)
    }, [farmApr, setCanFarm])

    const aprTooltip = useTooltip(
        <>
            {!!cakeApr && (
                <Text>
                    {t('Combined APR')}: <b>{displayApr.toFixed(2)}%</b>
                </Text>
            )}
            <ul>
                {!!cakeApr && (
                    <li>
                        {t('Farm APR')}:{' '}
                        <b>
                            <Text display="inline-block" style={{ textDecoration: 'none', fontWeight: 800 }}>
                                {cakeApr && cakeApr.toFixed(2)}%
                            </Text>
                        </b>
                    </li>
                )}
                <li>
                    {t('LP Fee APR')}: <b>{globalLpApr.toFixed(2)}%</b>
                </li>
            </ul>
            <br />
            <Text>
                {t('Calculated using the total active liquidity staked versus the xECP reward emissions for the farm.')}
            </Text>
            <Text mt="15px">{t('APRs for individual positions may vary depending on the configs.')}</Text>
        </>,
    )

    return (
        <>
            <TooltipText ref={aprTooltip.targetRef} decorationColor="secondary">
                <Flex ml="4px" mr="5px" style={{ gap: 5 }}>
                    <p className="text-pri font-bold">{displayApr.toFixed(2)}%</p>
                    {!!cakeApr && <Icon icon="game-icons:farm-tractor" width={23} className="text-failure" />}
                </Flex>
            </TooltipText>
            {aprTooltip.tooltipVisible && aprTooltip.tooltip}
        </>
    )
}

export default V3AprWithFarming
